import { useMemo, useState } from 'react'
import { Alert, Button, Collapse, Input, Modal, Space, Table, Tag, Tooltip, Typography, message } from 'antd'
import { FileExcelOutlined, FileTextOutlined, SaveOutlined } from '@ant-design/icons'
import { translate, translateTemplate } from '../../../../core'
import { exportToCsv, exportToExcel } from '../../../../core/until'
import { aiAssistantService } from '../service'
import { ChatResponse, Verdict } from '../type/ai-assistant'
import { exportFileName, formatCell, requestErrorMessage } from '../ai-assistant.utils'
import { AnswerText } from './AnswerText'
import styles from './AiAssistant.module.scss'

const VERDICT_TAG: Record<Verdict, { color: string; label: string }> = {
  answered: { color: 'green', label: 'ai_reports_verdict_answered' },
  clarify: { color: 'blue', label: 'ai_reports_verdict_clarify' },
  refused: { color: 'orange', label: 'ai_reports_verdict_refused' },
  error: { color: 'red', label: 'ai_reports_verdict_error' },
  disabled: { color: 'default', label: 'ai_reports_verdict_disabled' },
}

/** The agent's reply: the answer, the data behind it, the query, export and save. */
export const AnswerCard = ({ response, onSaved }: { response: ChatResponse; onSaved?: () => void }) => {
  const { answer, verdict, result, trace, queryLogId } = response
  const [busy, setBusy] = useState<'xlsx' | 'csv' | null>(null)
  const [saveOpen, setSaveOpen] = useState(false)
  const [name, setName] = useState('')
  const [saving, setSaving] = useState(false)
  const tag = VERDICT_TAG[verdict] ?? VERDICT_TAG.error
  const failedSteps = trace.filter((step) => !step.ok)

  const columns = useMemo(
    () =>
      (result?.columns ?? []).map((column) => ({
        title: column,
        dataIndex: column,
        key: column,
        render: (value: unknown) => formatCell(value),
      })),
    [result],
  )
  const rows = useMemo(() => (result?.rows ?? []).map((row, index) => ({ ...row, __key: index })), [result])

  /**
   * Export the FULL result, not just the rows on screen: the chat table stops at
   * 200 rows, so ask the server to re-run the query it logged (up to 5,000 rows).
   * Falls back to the rows in hand if that call fails.
   */
  const exportRows = async (format: 'xlsx' | 'csv') => {
    if (!result?.rows.length) return
    setBusy(format)
    let rows = result.rows
    let columns = result.columns
    try {
      if (queryLogId) {
        const full = await aiAssistantService.exportRows(queryLogId)
        rows = full.rows
        columns = full.columns
      }
    } catch (error) {
      message.warning(translate('ai_reports_export_partial'))
    } finally {
      setBusy(null)
    }
    if (!rows.length) {
      message.warning(translate('ai_reports_export_empty'))
      return
    }
    const fileName = exportFileName()
    if (format === 'xlsx') exportToExcel({ data: rows, fileName, columns })
    else exportToCsv({ data: rows, fileName, columns })
  }

  const save = async () => {
    if (!queryLogId || !name.trim()) return
    setSaving(true)
    try {
      await aiAssistantService.saveReport(queryLogId, name.trim())
      message.success(translate('ai_reports_saved'))
      setSaveOpen(false)
      setName('')
      onSaved?.()
    } catch (error) {
      message.error(requestErrorMessage(error))
    } finally {
      setSaving(false)
    }
  }

  const queryPanel = (result || failedSteps.length > 0) && (
    <Collapse
      size="small"
      ghost
      items={[
        {
          key: 'query',
          label: translate('ai_reports_show_query'),
          children: (
            <div className={styles.queryPanel}>
              {result && (
                <>
                  <Typography.Paragraph type="secondary" className="mb-1">
                    {result.explanation}
                  </Typography.Paragraph>
                  <Typography.Paragraph copyable={{ text: result.sql }} className="mb-2">
                    <pre className={styles.sql}>{result.sql}</pre>
                  </Typography.Paragraph>
                </>
              )}
              {failedSteps.length > 0 && (
                <>
                  <Typography.Text type="secondary">{translate('ai_reports_failed_attempts')}</Typography.Text>
                  {failedSteps.map((step, index) => (
                    <div key={index} className="mt-1">
                      <pre className={styles.sql}>{step.sql}</pre>
                      <Typography.Text type="danger">{step.error}</Typography.Text>
                    </div>
                  ))}
                </>
              )}
            </div>
          ),
        },
      ]}
    />
  )

  return (
    <div className={styles.answerCard}>
      <div className={styles.answerHeader}>
        <Space size="small" wrap>
          <Tag color={tag.color}>{translate(tag.label)}</Tag>
          {result && (
            <Typography.Text type="secondary">
              {translateTemplate('ai_reports_rows', { count: result.rowCount })}
            </Typography.Text>
          )}
        </Space>
        {result && result.rows.length > 0 && (
          <Space size="small">
            <Tooltip title={translate('export_to_excel')}>
              <Button size="small" icon={<FileExcelOutlined />} loading={busy === 'xlsx'} onClick={() => exportRows('xlsx')}>
                Excel
              </Button>
            </Tooltip>
            <Tooltip title={translate('ai_reports_export_csv')}>
              <Button size="small" icon={<FileTextOutlined />} loading={busy === 'csv'} onClick={() => exportRows('csv')}>
                CSV
              </Button>
            </Tooltip>
            {queryLogId && (
              <Tooltip title={translate('ai_reports_save_hint')}>
                <Button size="small" icon={<SaveOutlined />} onClick={() => setSaveOpen(true)}>
                  {translate('save')}
                </Button>
              </Tooltip>
            )}
          </Space>
        )}
      </div>

      <AnswerText text={answer} />

      {result && result.truncated && (
        <Alert
          className="mb-2"
          type="warning"
          showIcon
          message={translateTemplate('ai_reports_truncated', { count: result.rowCount })}
        />
      )}

      {result && result.rows.length > 0 && (
        <Table
          size="small"
          rowKey="__key"
          columns={columns}
          dataSource={rows}
          scroll={{ x: 'max-content' }}
          pagination={{ pageSize: 10, showSizeChanger: true, hideOnSinglePage: true }}
        />
      )}

      {queryPanel}

      <Modal
        open={saveOpen}
        title={translate('ai_reports_save_title')}
        okText={translate('save')}
        confirmLoading={saving}
        okButtonProps={{ disabled: !name.trim() }}
        onOk={save}
        onCancel={() => setSaveOpen(false)}
      >
        <Typography.Paragraph type="secondary">{translate('ai_reports_save_description')}</Typography.Paragraph>
        <Input
          value={name}
          onChange={(event) => setName(event.target.value)}
          onPressEnter={save}
          placeholder={translate('ai_reports_save_placeholder')}
          maxLength={80}
          autoFocus
        />
      </Modal>
    </div>
  )
}
